<template>
    <b-card
        no-body
    >
        <div class="card-header">
            <div
                class="card-group-title"
            >
                <h4>
                    {{ $t('org.billing.Term') }}
                </h4>
            </div>
            <a
                data-action="collapse"
                :class="{ rotate: isCollapsed }"
                @click="triggerCollapse"
                class="mr-2"
            >
                <feather-icon
                    icon="ChevronDownIcon"
                    size="16"
                />
            </a>
        </div>
        <b-alert
            :show="showDismissibleFilter"
            variant="info"
            dismissible
            @dismissed="dismissed"
        >
            <div class="alert-body">
                <feather-icon
                    icon="SearchIcon"
                />
                {{ filter }}
            </div>
        </b-alert>
        <div class="card-body" v-if="noData" style="text-align:center;">
            {{ $t("org.message.No Data") }}
        </div>
        <div
            v-else
            ref="REF_DYNAMICSEARCH_FULL_DAILY_LINE"
            class="ml-2"
        >
            <VsSvgLine
                :data="chartData"
                :xKey="xKey"
                :yKeys="yKeys"
                :lineColor="lineColor"
                :width="width"
                :height="height"
                :labelRotate="0"
                :tickSize="0.7"
                :tickPadding="15"
                :strokeWidth="3"
                :yGrid="6"
                area
                circle
                xAxis
                yAxis
                xLabel
                yLabel
                :labelColor="themeColors.currentColor"
                @select:range="handlerSelectRange"
                @click:point="clickPoint"
                @mouseover:point="hoverPoint"
                @mouseleave.native="outPoint"
            >
                <template v-slot:x-axis="{ item }">
                    <template v-if="chartData.length >= 50 && item.index % 14 === 0">
                        {{ formattedDate(lang, item.label, 'month-day') }}
                    </template>
                    <template v-else-if="chartData.length < 50 && chartData.length >= 30 && item.index % 7 === 0">
                        {{ formattedDate(lang, item.label, 'month-day') }}
                    </template>
                    <template v-else-if="chartData.length < 30 && chartData.length >= 15 && item.index % 5 === 0">
                        {{ formattedDate(lang, item.label, 'month-day') }}
                    </template>
                    <template v-else-if="chartData.length < 15 && item.index % 2 === 0">
                        {{ formattedDate(lang, item.label, 'month-day') }}
                    </template>
                </template>
                <template v-slot:y-axis="{ item }">
                    {{ __toKFormat(item.label) }}
                </template>
                <template v-slot:tooltip="{ item }">
                    <g v-if="tooltipData.index > -1">
                        <rect
                            :x="tooltipData.index < chartData.length / 2 ? item.x + 10 : item.x - 160"
                            :y="height - item.y < height / 2 ? item.y - (yKeys.length * 17 + 35) - padding : padding + 5"
                            :fill="backgroundColor"
                            :height="yKeys.length * 17 + 27"
                            width="150"
                            opacity="0.9"
                            rx="5"
                            ry="5"
                        ></rect>
                        <text
                            font-size="12"
                            font-weight="bold"
                            :fill="themeColors.currentColor"
                            :text-anchor="tooltipData.index < chartData.length / 2 ? 'start' : 'end'"
                            :x="tooltipData.index < chartData.length / 2 ? item.x + 15 : item.x - 15"
                            :y="height - item.y < height / 2 ? item.y - (yKeys.length * 17) - padding * 2 : padding * 2"
                        >
                            {{ item.label }}
                        </text>
                        <text
                            font-size="14"
                            font-weight="bold"
                            :fill="themeColors.currentColor"
                            :text-anchor="tooltipData.index < chartData.length / 2 ? 'start' : 'end'"
                            :x="tooltipData.index < chartData.length / 2 ? item.x + 15 : item.x - 15"
                            :y="height - item.y < height / 2 ? item.y - (yKeys.length * 17) - padding * 2 + (0 * 17 + 20) : padding * 2 + (0 * 17 + 20)"
                        >
                            {{ $_getDollar(item.data.cost) }}
                        </text>
                    </g>
                </template>
            </VsSvgLine>
        </div>
        <b-collapse
            v-model="visible"
            :visible="visible"
            class="card-content"
        >
            <b-table-simple
                sticky-header
            >
                <b-tbody>
                    <b-tr v-for="(item, index) in chartData" :key="index">
                        <b-td style="word-break:break-all;">
                            {{ item.key }}
                        </b-td>
                        <b-td class="text-right" style="width:30%;">
                            {{ $_getDollar(item.cost) }}
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </b-collapse>
    </b-card>
</template>

<script>
import useSelectProjectAndGroup from '@/composables/useSelectProjectAndGroup'
import { getCurrentInstance, computed, ref, toRefs, onUpdated, onMounted, onUnmounted, watch } from '@vue/composition-api'
import useCommonData from '@/composables/useCommonData'
import { VsSvgLine } from '@/libs/vuesign/chart'
import formattedDate from '@/utils/formattedDate'
export default {
    components: {
        VsSvgLine
    },
    props: {
        fullData: {
            type: Array,
            default: function () {
                return []
            }
        },
        filterData: {
            type: Object,
            default: function () {
                return {
                    monthlyCost: [],
                    dailyCost: []
                }
            }
        },
        themeColors: {
            type: Object,
            default: function () {
                return {}
            }
        },
        theme: {
            type: String
        },
        changeProject: {
            type: Boolean
        }
    },
    setup (props, context) {
        const { changeProject } = useSelectProjectAndGroup()
        const { __toKFormat } = useCommonData()
        const { themeColors, theme, filterData, fullData } = toRefs(props)
        const vm = getCurrentInstance().proxy
        const monthlyCost = computed(() => filterData.value.monthlyCost)
        const dailyCost = computed(() => filterData.value.dailyCost)
        const width = ref(0)
        const height = 250
        const padding = 20
        const visible = ref(false)
        const isCollapsed = ref(false)
        const filter = ref('')
        const showDismissibleFilter = ref(false)
        const lang = computed(() => vm.$i18n.locale)
        // chart
        const xKey = 'key'
        const yKeys = ['cost']
        const monthKeys = computed(() => monthlyCost.value.map(item => item.key))
        const chartData = computed(() => {
            let list = []
            monthKeys.value.forEach(key => {
                list = list.concat(dailyCost.value[key])
            })
            const costData = list.reduce((accumulator, currentValue) => {
                if (!accumulator[currentValue.key]) accumulator[currentValue.key] = 0
                accumulator[currentValue.key] += currentValue.cost
                return accumulator
            }, {})
            return Object.keys(costData).map(key => {
                return {
                    key,
                    cost: costData[key]
                }
            })
        })
        const backgroundColor = computed(() => themeColors.value[theme.value])
        const lineColor = {
            cost: themeColors.value.primary
        }
        // table
        const noData = computed(() => !fullData.value.length)
        const triggerCollapse = () => {
            visible.value = !visible.value
        }
        // tooltip
        const tooltipData = ref({})
        const hoverPoint = ({ event, data }) => {
            tooltipData.value = data
        }
        const outPoint = () => {
            tooltipData.value = {}
        }
        // resize
        const REF_DYNAMICSEARCH_FULL_DAILY_LINE = ref(null)
        const resize = () => {
            if (REF_DYNAMICSEARCH_FULL_DAILY_LINE.value) {
                width.value = REF_DYNAMICSEARCH_FULL_DAILY_LINE.value.getBoundingClientRect().width
            }
        }
        onUpdated(resize)
        onMounted(() => {
            vm.$nextTick(() => {
                resize()
                window.addEventListener('resize', resize)
            })
        })
        onUnmounted(() => {
            window.removeEventListener('resize', resize)
        })
        // range filter event
        const handlerSelectRange = ({ data }) => {
            const usageDate = {
                start: data[0].label,
                end: data[1].label
            }
            if (usageDate.start === usageDate.end) {
                showDismissibleFilter.value = true
                filter.value = usageDate.start
            } else {
                showDismissibleFilter.value = true
                filter.value = `${usageDate.start} - ${usageDate.end}`
            }
            context.emit('filter:items', { field: 'usageDate', value: usageDate })
        }
        const handlerResetFilter = () => {
            showDismissibleFilter.value = false
            filter.value = undefined
            context.emit('filter:items', { field: 'usageDate', value: undefined })
        }
        const clickPoint = ({ event, data }) => {
            if (chartData.value.length === 1) {
                filter.value = ''
                showDismissibleFilter.value = false
                context.emit('filter:items', { field: 'usageDate', value: undefined })
            } else {
                const usageDate = {
                    start: data.key,
                    end: data.key
                }
                filter.value = `${usageDate.start}`
                showDismissibleFilter.value = true
                context.emit('filter:items', { field: 'usageDate', value: usageDate })
            }
        }
        const dismissed = () => {
            if (!filter.value) return
            handlerResetFilter()
        }
        // watch
        watch(changeProject, (_) => {
            filter.value = undefined
            showDismissibleFilter.value = false
        })
        return {
            REF_DYNAMICSEARCH_FULL_DAILY_LINE,
            dismissed,
            width,
            height,
            padding,
            xKey,
            yKeys,
            visible,
            isCollapsed,
            __toKFormat,
            backgroundColor,
            lineColor,
            chartData,
            noData,
            tooltipData,
            hoverPoint,
            outPoint,
            triggerCollapse,
            showDismissibleFilter,
            filter,
            handlerSelectRange,
            clickPoint,
            lang,
            formattedDate
        }
    },
    methods: {

    }
}
</script>

<style>

</style>
